import { useEffect, useState } from 'react';
import { Check, ShieldCheck, Crown, Shield, Star, Gem, Heart } from 'lucide-react';

const STORAGE_KEY = 'show-options:active';

// --- Palette: icy blue background, deep navy ink, vibrant green premiums ---
const BG_TOP = '#F4F7FC';
const BG_MID = '#EAF1FA';
const BG_DEEP = '#DCE7F4';
const NAVY = '#0B1437';
const NAVY_LINK = '#1746A2';        // brand blue for labels like "DEATH BENEFIT"
const INK = '#0B1437';
const MUTED = '#5C6473';
const GREEN_PREMIUM = '#16A34A';    // premium dollar value
const GREEN_CHECK = '#16A34A';
const CARD_BG = '#FFFFFF';
const HAIRLINE = '#E6ECF3';

// Per-tier theme: rich metallic banner gradient + accent tones + bottom tagline
const TIER_THEMES = [
  {
    name: 'Gold',
    banner: 'linear-gradient(135deg, #F4D86D 0%, #E0A632 35%, #C68314 65%, #E0A632 100%)',
    bannerShine: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 50%)',
    label: '#C68314',
    divider: '#E9D6A8',
    cardOuter: 'linear-gradient(180deg, #FFFBF0 0%, #FAF3E0 100%)',
    insetTint: '#FBF6E8',
    Medallion: Crown,
    medBg: 'radial-gradient(circle at 30% 30%, #F4D86D 0%, #C68314 80%)',
    TaglineIcon: Star,
    taglineIconBg: 'radial-gradient(circle at 30% 30%, #F4D86D 0%, #C68314 80%)',
    taglineIconColor: '#FFFFFF',
    headline: 'Max Coverage. Max Value.',
    subhead: 'Your Best Protection.',
  },
  {
    name: 'Silver',
    banner: 'linear-gradient(135deg, #C5CDDC 0%, #6E7B95 35%, #44516B 65%, #6E7B95 100%)',
    bannerShine: 'linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 50%)',
    label: '#5567A5',
    divider: '#D8DCE3',
    cardOuter: 'linear-gradient(180deg, #FAFCFF 0%, #EFF4FB 100%)',
    insetTint: '#F4F7FC',
    Medallion: Star,
    medBg: 'radial-gradient(circle at 30% 30%, #B9C5DC 0%, #3F506E 80%)',
    TaglineIcon: Gem,
    taglineIconBg: 'radial-gradient(circle at 30% 30%, #6E8FCE 0%, #1F3D88 80%)',
    taglineIconColor: '#E1ECFF',
    headline: 'The Smart Choice.',
    subhead: 'Great Coverage. Great Price.',
  },
  {
    name: 'Bronze',
    banner: 'linear-gradient(135deg, #DD9B70 0%, #A85B30 35%, #7A3F1E 65%, #A85B30 100%)',
    bannerShine: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%)',
    label: '#A85B30',
    divider: '#E5C9B0',
    cardOuter: 'linear-gradient(180deg, #FFF8F1 0%, #F8ECDE 100%)',
    insetTint: '#FAF1E5',
    Medallion: Shield,
    medBg: 'radial-gradient(circle at 30% 30%, #DD9B70 0%, #7A3F1E 80%)',
    TaglineIcon: Heart,
    taglineIconBg: 'radial-gradient(circle at 30% 30%, #DD9B70 0%, #7A3F1E 80%)',
    taglineIconColor: '#FFFFFF',
    headline: 'Affordable Protection.',
    subhead: 'Peace of Mind.',
  },
];

const formatUSD = (n, opts = {}) => {
  const num = Number(n || 0);
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: opts.minFractionDigits ?? 0, maximumFractionDigits: opts.maxFractionDigits ?? 0 })}`;
};

export default function ShowOptionsPresent() {
  const [state, setState] = useState(null);

  useEffect(() => {
    const load = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) setState(JSON.parse(raw));
      } catch (e) { console.error('Failed to restore presentation state:', e); }
    };
    load();
    const onStorage = (e) => { if (e.key === STORAGE_KEY) load(); };
    window.addEventListener('storage', onStorage);
    document.title = 'Your Options';
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!state || !state.tiers) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: BG_TOP }}>
        <div className="text-center" style={{ color: MUTED }}>
          <div className="text-base">No options to present yet.</div>
          <div className="mt-1 text-sm">Open Show Options in the agent view, fill in the tiers, then click Launch Presentation.</div>
        </div>
      </div>
    );
  }

  const { carrier_name, carrier_logo, financial_rating, product_type, term_length, client_name, agent_name, recommended_tier, tiers } = state;

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{
        background: `linear-gradient(160deg, ${BG_TOP} 0%, ${BG_MID} 55%, ${BG_DEEP} 100%)`,
        color: INK,
        fontSize: 18,
        fontFamily: '"Inter", "Manrope", system-ui, sans-serif',
      }}
      data-testid="show-options-present"
    >
      {/* Flowing wave decorations on left & right edges */}
      <svg className="pointer-events-none absolute left-0 top-0 h-full w-[18%] opacity-70" viewBox="0 0 300 1000" preserveAspectRatio="none">
        <defs>
          <linearGradient id="waveLeft" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#A8C2E8" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#A8C2E8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,0 C 120,150 -40,350 100,500 C 240,650 60,850 0,1000 L 0,0 Z" fill="url(#waveLeft)" />
        <path d="M0,80 C 80,220 -20,420 80,580 C 180,740 40,900 0,1000" stroke="#9BBCE0" strokeOpacity="0.4" strokeWidth="1.5" fill="none" />
        <path d="M0,160 C 60,280 -10,480 60,640 C 130,780 20,920 0,1000" stroke="#B8D0EA" strokeOpacity="0.3" strokeWidth="1" fill="none" />
      </svg>
      <svg className="pointer-events-none absolute right-0 top-0 h-full w-[18%] opacity-70" viewBox="0 0 300 1000" preserveAspectRatio="none">
        <defs>
          <linearGradient id="waveRight" x1="1" x2="0" y1="0" y2="0">
            <stop offset="0%" stopColor="#A8C2E8" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#A8C2E8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M300,0 C 180,150 340,350 200,500 C 60,650 240,850 300,1000 L 300,0 Z" fill="url(#waveRight)" />
        <path d="M300,80 C 220,220 320,420 220,580 C 120,740 260,900 300,1000" stroke="#9BBCE0" strokeOpacity="0.4" strokeWidth="1.5" fill="none" />
        <path d="M300,160 C 240,280 310,480 240,640 C 170,780 280,920 300,1000" stroke="#B8D0EA" strokeOpacity="0.3" strokeWidth="1" fill="none" />
      </svg>

      <div className="relative max-w-[1400px] mx-auto flex min-h-screen flex-col px-6 py-5 sm:px-10 sm:py-7">

        {/* ---------- Header ---------- */}
        <div className="text-center">
          {carrier_logo && (
            <img
              src={carrier_logo}
              alt={carrier_name}
              className="mx-auto h-10 max-w-[200px] object-contain sm:h-12"
              draggable={false}
            />
          )}
          <h1 className="mt-2 font-['Manrope'] font-extrabold tracking-tight" style={{ color: NAVY, fontSize: 'clamp(36px, 4.5vw, 52px)', letterSpacing: '-0.02em', lineHeight: 1 }}>
            {carrier_name}
          </h1>
          {product_type && (
            <div className="mt-3 mx-auto flex items-center justify-center gap-3" style={{ maxWidth: 560 }}>
              <span className="h-px flex-1" style={{ background: '#BFD0E5' }} />
              <span className="text-[14px] font-bold uppercase tracking-[0.32em]" style={{ color: NAVY }}>
                {product_type}{term_length ? ` · ${term_length}` : ''}
              </span>
              <span className="h-px flex-1" style={{ background: '#BFD0E5' }} />
            </div>
          )}
          {financial_rating && (
            <div
              className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2"
              style={{ background: CARD_BG, border: `1px solid ${HAIRLINE}`, boxShadow: '0 2px 8px rgba(11,20,55,0.05)' }}
            >
              <ShieldCheck className="h-4 w-4" style={{ color: GREEN_CHECK }} />
              <span className="text-[14px] font-semibold" style={{ color: NAVY }}>{financial_rating} AM-Best Rated</span>
            </div>
          )}
        </div>

        {/* ---------- Three Tier Cards ---------- */}
        <div className="mt-5 grid flex-1 grid-cols-1 gap-5 md:grid-cols-3 md:gap-4 lg:gap-5">
          {tiers.map((tier, idx) => {
            const theme = TIER_THEMES[idx] || TIER_THEMES[0];
            const isRec = recommended_tier === idx;
            const Medallion = theme.Medallion;
            const TaglineIcon = theme.TaglineIcon;
            return (
              <article
                key={tier?.tier_name || `tier-${idx}`}
                className="relative flex flex-col rounded-[28px]"
                style={{
                  background: theme.cardOuter,
                  boxShadow: isRec
                    ? `0 0 0 3px #5A8DDB, 0 0 36px 6px rgba(90, 141, 219, 0.55), 0 14px 38px rgba(11, 20, 55, 0.10)`
                    : `0 14px 38px rgba(11, 20, 55, 0.07), 0 4px 12px rgba(11, 20, 55, 0.04)`,
                  border: isRec ? 'none' : `1px solid ${HAIRLINE}`,
                  animation: isRec ? 'recGlow 2.6s ease-in-out infinite' : 'none',
                }}
                data-testid={`present-tier-${idx}`}
              >
                {/* Recommended ribbon — top-right corner */}
                {isRec && (
                  <div
                    className="absolute right-0 top-3 z-10 flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-white"
                    style={{
                      background: 'linear-gradient(135deg, #1746A2 0%, #0B1437 100%)',
                      boxShadow: '0 6px 14px rgba(11, 20, 55, 0.3)',
                    }}
                  >
                    <Star className="h-3 w-3" fill="#F4D86D" stroke="none" />
                    Recommended
                  </div>
                )}

                {/* Metallic banner with medallion + tier name */}
                <div
                  className="relative flex h-[78px] items-center gap-3 overflow-hidden rounded-t-[28px] pl-5 pr-6"
                  style={{ background: theme.banner }}
                >
                  {/* Shine overlay */}
                  <div className="pointer-events-none absolute inset-0" style={{ background: theme.bannerShine }} />

                  {/* Medallion — pops below the banner edge */}
                  <div
                    className="relative -mb-6 flex h-16 w-16 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: theme.medBg,
                      boxShadow: '0 6px 14px rgba(0,0,0,0.25), inset 0 2px 4px rgba(255,255,255,0.45), inset 0 -3px 6px rgba(0,0,0,0.2)',
                      border: '2px solid rgba(255,255,255,0.7)',
                    }}
                  >
                    <Medallion className="h-7 w-7 text-white" strokeWidth={2.5} fill={theme.name === 'Silver' ? 'white' : 'currentColor'} />
                  </div>

                  <div
                    className="relative font-['Manrope'] text-3xl font-extrabold uppercase tracking-[0.18em] text-white"
                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.35)' }}
                  >
                    {tier.label}
                  </div>
                </div>

                {/* Numbers section (white with subtle tint) */}
                <div className="px-6 pt-6 pb-4 text-center">
                  <div className="text-[14px] font-bold uppercase tracking-[0.2em]" style={{ color: NAVY_LINK }}>
                    Death Benefit
                  </div>
                  <div
                    className="mt-1 font-['Manrope'] font-extrabold leading-none tabular-nums"
                    style={{ color: GREEN_PREMIUM, fontSize: 'clamp(40px, 4.6vw, 56px)', letterSpacing: '-0.02em' }}
                  >
                    {formatUSD(tier.death_benefit)}
                  </div>

                  <div className="mx-auto mt-4 h-px w-3/4" style={{ background: theme.divider }} />

                  <div className="mt-4 text-[14px] font-bold uppercase tracking-[0.2em]" style={{ color: NAVY_LINK }}>
                    Premium
                  </div>
                  <div className="mt-1 flex items-baseline justify-center gap-1">
                    <span
                      className="font-['Manrope'] font-extrabold leading-none tabular-nums"
                      style={{ color: INK, fontSize: 'clamp(34px, 4vw, 48px)', letterSpacing: '-0.02em' }}
                    >
                      {formatUSD(tier.monthly_premium, { minFractionDigits: 2, maxFractionDigits: 2 })}
                    </span>
                    <span className="text-[15px] font-medium" style={{ color: MUTED }}>/month</span>
                  </div>
                </div>

                {/* Inset benefits panel */}
                <div className="mx-4 mb-4 flex-1 rounded-2xl px-5 py-4" style={{
                  background: CARD_BG,
                  border: `1px solid ${HAIRLINE}`,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 6px rgba(11,20,55,0.04)',
                }}>
                  <div className="text-[13px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: theme.label }}>
                    Included:
                  </div>
                  <ul className="space-y-2">
                    {(tier.benefits || []).length === 0 ? (
                      <li className="text-base italic" style={{ color: MUTED }}>—</li>
                    ) : (
                      (tier.benefits || []).map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-[15px] leading-snug" style={{ color: INK }}>
                          <span
                            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                            style={{ background: `${GREEN_CHECK}20`, border: `1.5px solid ${GREEN_CHECK}40` }}
                          >
                            <Check className="h-3 w-3" strokeWidth={3.5} style={{ color: GREEN_CHECK }} />
                          </span>
                          <span>{b}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>

                {/* Tagline footer with icon */}
                <div className="flex items-center gap-3 px-5 pb-5 pt-1">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: theme.taglineIconBg,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.18), inset 0 2px 3px rgba(255,255,255,0.4)',
                      border: '2px solid rgba(255,255,255,0.5)',
                    }}
                  >
                    <TaglineIcon className="h-5 w-5" strokeWidth={2.5} style={{ color: theme.taglineIconColor }} fill={theme.taglineIconColor} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-['Manrope'] text-[16px] font-bold leading-tight" style={{ color: NAVY }}>
                      {theme.headline}
                    </div>
                    <div className="text-[14px] italic" style={{ color: MUTED }}>
                      {theme.subhead}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* ---------- Footer ---------- */}
        <div className="mt-6 flex flex-col items-center text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.34em]" style={{ color: MUTED }}>
            Personally prepared for
          </div>
          <div className="mt-2 font-['Manrope'] font-extrabold tracking-tight" style={{ color: NAVY, fontSize: 'clamp(22px, 2.4vw, 30px)', letterSpacing: '-0.015em' }}>
            {client_name || 'You & your family'}
          </div>
          {agent_name && (
            <div className="mt-2 text-[15px]" style={{ color: MUTED }}>
              Presented by <span className="font-bold" style={{ color: NAVY }}>{agent_name}</span>
            </div>
          )}
          <div className="mt-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em]" style={{ color: NAVY }}>
            <span className="h-px w-10" style={{ background: '#BFD0E5' }} />
            Breeze Wealth Management
            <span className="h-px w-10" style={{ background: '#BFD0E5' }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes recGlow {
          0%, 100% { box-shadow: 0 0 0 3px #5A8DDB, 0 0 32px 4px rgba(90, 141, 219, 0.45), 0 14px 38px rgba(11, 20, 55, 0.10); }
          50%      { box-shadow: 0 0 0 3px #5A8DDB, 0 0 48px 10px rgba(90, 141, 219, 0.70), 0 14px 38px rgba(11, 20, 55, 0.10); }
        }
      `}</style>
    </div>
  );
}
